/* ==========================================================================
   Quiz landing page — results screen

   Drives the shop the quiz hands over to. The cards are server-rendered
   (snippets/quiz-lp-card.liquid, the Back to School card ported across), so
   this file does not build markup — it does what the B2S section does for its
   own grid, plus the two things only the quiz knows:

     · the ORDER of the cards, from the shopper's answers scored against
       assets/quiz.json, and
     · which cards carry the "Right fit" mark.

   Where the answers come from
   ---------------------------
   localStorage, written by quiz-lp.js on completion. The handover URL is just
   ?step=results — no answers ride in the query string, so the address stays
   short and shareable. Answers ARE still read from the query string when they
   happen to be there, which keeps a hand-typed URL usable for testing.

   Because the store is local rather than per-session, a shopper who closes the
   tab and returns to the link later still lands on their own ranked grid.

   Opened without either (a link passed to someone else, cleared site data) the
   screen still works: every style is on the page in listing order, with no fit
   marks and a prompt to take the quiz. Nothing is ever filtered away.
   ========================================================================== */
(function () {
  'use strict';

  /* The ranges, and the order they sit in when the grid shows them all. One
     source for both, so a range can never be scored but left unplaceable. */
  var RANGE_ORDER = { ladies: 0, mens: 1, kids: 2 };
  var RANGES = Object.keys(RANGE_ORDER);

  /* Apply, verify, retry. The successful attempts have always been the ones
     seconds after a cart mutation, never milliseconds, so the backoff runs
     long. */
  var DISCOUNT_BACKOFF = [400, 900, 1800, 3000];
  /* How long the drawer waits on the discount before opening anyway. */
  var DRAWER_DISCOUNT_WAIT = 900;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* A fetch borrowed from a hidden iframe. The drawer app hooks window.fetch to
     spot cart writes and pops itself open; requests made through another
     window's fetch go unnoticed, which is what keeps the load-time cart clear
     from flashing the drawer at every visitor. */
  var silentFetchReady = null;
  function getSilentFetch() {
    if (silentFetchReady) return silentFetchReady;
    silentFetchReady = new Promise(function (resolve) {
      try {
        var frame = document.createElement('iframe');
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('tabindex', '-1');
        frame.style.cssText = 'position:absolute;left:-9999px;width:0;height:0;border:0;';
        var settled = false;
        var grab = function () {
          if (settled) return;
          settled = true;
          try {
            resolve(frame.contentWindow.fetch.bind(frame.contentWindow));
          } catch (e) {
            resolve(window.fetch.bind(window));
          }
        };
        frame.addEventListener('load', grab);
        document.body.appendChild(frame); /* stays attached — removing it kills the fetch */
        setTimeout(grab, 400); /* some browsers never fire load on about:blank */
      } catch (e) {
        resolve(window.fetch.bind(window));
      }
    });
    return silentFetchReady;
  }
  function silentFetch(url, opts) {
    return getSilentFetch().then(function (f) { return f(url, opts); });
  }

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* The tray's totals are the only prices this file writes — every price on a
     card is already rendered by Liquid's `money` filter, which is market-aware.
     So the tray reuses the shop's own money_format to match them exactly.

     No currency is ever hardcoded. If the format is missing the amount goes
     through Intl with the ACTIVE market's currency (Shopify.currency.active at
     runtime, else the cart's ISO code from Liquid), and only if even that is
     unavailable does it fall back to a bare number — a wrong symbol is worse
     than none. */
  function moneyFormatter(format, currency) {
    function withDelimiters(n, precision, thousands, decimal) {
      var parts = n.toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || currency;

    return function (cents) {
      var value = (cents || 0) / 100;

      if (format) {
        return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
          switch (name) {
            case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
            case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
            case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
            default: return withDelimiters(value, 2, ',', '.');
          }
        });
      }

      if (active) {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: active
          }).format(value);
        } catch (e) {
          /* an unknown ISO code — fall through to the bare number */
        }
      }
      return withDelimiters(value, 2, ',', '.');
    };
  }

  function Results(root) {
    this.root = root;
    this.config = JSON.parse(root.querySelector('[data-quiz-lp-results-config]').textContent);
    this.labels = this.config.labels || {};
    this.money = moneyFormatter(this.config.moneyFormat, this.config.currency);

    this.lead = root.querySelector('[data-results-lead]');
    this.sub = root.querySelector('[data-results-sub]');
    this.answersBox = root.querySelector('[data-results-answers]');
    this.answersList = root.querySelector('[data-results-answers-list]');
    this.filterBar = root.querySelector('[data-results-filters]');
    this.grid = root.querySelector('[data-results-grid]');
    this.emptyEl = root.querySelector('[data-results-empty]');
    this.dock = root.querySelector('[data-results-dock]');
    this.toastEl = root.querySelector('[data-results-toast]');

    this.items = Array.prototype.slice.call(this.grid.querySelectorAll('.quiz-lp-results__grid-item'));
    /* Staged pairs, in memory only: a reload starts from nothing rather than
       resurrecting a selection the shopper cannot see. */
    this.selected = [];
    /* Mirror of the live Shopify cart. The tray counts what is already in the
       cart alongside what is staged — otherwise adding two pairs would reset
       the bar to "nothing selected" while the cart quietly holds them, and the
       next pair would look like the first. */
    this.cartItemCount = 0;
    this.cartVariantIds = {};
    this.cartPrices = [];
    /* Handed to /cart/add.js but not yet visible in a cart.js read. Without
       this the cards blink off and back on around every add. */
    this.pending = [];
    this.keepSelected = this.config.keepSelectedAfterAdd !== false;

    /* Discount codes, in the order the merchant listed them — the order is
       preserved all the way to Shopify, one request per code. */
    var discount = this.config.discount || {};
    this.discountCodes = String(discount.codes || '')
      .split(',')
      .map(function (code) { return code.trim(); })
      .filter(Boolean);
    this.discountEnabled = discount.enabled !== false && this.discountCodes.length > 0;
    this.discountMinItems = discount.minItems || 0;
    this.discountInFlight = null;
    this.discountSeeded = false;
    /* Has a REAL /discount/ round trip happened since this page loaded? A cart
       can list a code in discount_codes while its totals were never
       recalculated — a leftover attachment from the previous session. Trusting
       that listing is what makes a first apply silently skip. So the first
       qualifying sync of every load always applies for real. */
    this.appliedThisLoad = false;
    this.cartReady = Promise.resolve();
    this.filter = 'all';
    this.answers = null;
    this.answerLabels = null;

    /* Narrow phones get the short button labels — "SELECT THIS PAIR" cannot fit
       a 2-up grid at 320px whatever the font size. */
    this.narrow = window.matchMedia('(max-width: ' + (this.config.shortLabelBreakpoint || 430) + 'px)');
    var refresh = this.refreshCardStates.bind(this);
    if (this.narrow.addEventListener) this.narrow.addEventListener('change', refresh);
    else if (this.narrow.addListener) this.narrow.addListener(refresh);

    /* The drawer app opens itself the moment it sniffs a /cart/add.js call, so
       after an add it is usually already open. Subscribing to its own events is
       more reliable than guessing: it tells us not to re-open (which toggles
       some builds shut) and not to force it back up if the shopper closed it
       while the request was still in flight. */
    this.drawerOpen = false;
    this.drawerDismissed = false;
    var self = this;
    try {
      if (typeof window.upcartOnCartOpened === 'function') {
        window.upcartOnCartOpened(function () { self.drawerOpen = true; });
      }
      if (typeof window.upcartOnCartClosed === 'function') {
        window.upcartOnCartClosed(function () {
          self.drawerOpen = false;
          self.drawerDismissed = true;
        });
      }
    } catch (e) {
      /* no drawer app on the page — openCartDrawer falls back below */
    }

    root.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('quiz-lp:complete', this.onComplete.bind(this));

    this.bindSwatches();
    this.start();
  }

  /* ------------------------------------------------------------- the answers */

  Results.prototype.readAnswers = function () {
    /* A hand-typed query string wins, so a tester can force a combination
       without clearing the session. Otherwise the stored run. */
    var fromUrl = window.QuizLP && window.QuizLP.answersFromUrl();
    if (fromUrl) return { answers: fromUrl, labels: null };

    var stored = window.QuizLP && window.QuizLP.getResult();
    if (stored && stored.answers) return { answers: stored.answers, labels: stored.labels };
    return { answers: null, labels: null };
  };

  /* The range tabs pin beneath the header, so they need its height — and that
     is not a constant we can hardcode: the bar is shorter on phones, and the
     merchant can unpin the header entirely, in which case the tabs belong at
     the very top. Measure the real element and publish the number for the CSS.
     The guarantee banner reads the same property to land its scroll. */
  Results.prototype.trackStickyTop = function () {
    var self = this;
    var measure = function () {
      var bar = document.querySelector('.quiz-lp-brand.is-results');
      var wrapper = bar && bar.parentElement;
      var top = 0;
      if (wrapper && window.getComputedStyle(wrapper).position === 'sticky') {
        top = Math.round(wrapper.getBoundingClientRect().height);
      }
      self.root.style.setProperty('--qzr-stick-top', top + 'px');
    };

    measure();
    /* The header marks itself `is-results` from its own inline script; on a
       cold load that can land either side of this one. Re-measure once the
       page has settled rather than racing it. */
    window.addEventListener('load', measure);
    window.addEventListener('resize', measure);
  };

  Results.prototype.start = function () {
    /* Off the results URL the quiz owns the screen. The instance still exists
       so the `quiz-lp:complete` listener is live — a quiz configured to hand
       over in place, with no redirect, unhides this and renders then. */
    if (this.root.hidden) return;

    this.trackStickyTop();

    /* Held in cartReady so nothing can mutate the cart before the clear lands —
       a fast click on Add to cart would otherwise be wiped by it. The mirror
       runs after, so the tray and the cards start from the real cart. */
    var self = this;
    this.cartReady = this.clearCartOnLoad().then(function () { return self.mirrorCart(); });

    watchCartMutations();
    window.addEventListener('quiz-lp:cartchanged', function () {
      self.mirrorCart().then(function () { return self.syncDiscount(); });
    });

    var read = this.readAnswers();
    this.answers = read.answers;
    this.answerLabels = read.labels;

    this.renderHead();
    this.refreshCardStates();
    this.renderDock();
    this.revealDock();
    this.setFilter((this.answers && this.answers.range) || 'all');

    if (!this.answers) return;

    /* Inline from the metaobject: rank now, so the grid never paints in listing
       order and then rearranges under the shopper. */
    if (this.config.ranking) {
      this.applyRanking(this.config.ranking);
      return;
    }

    if (!this.config.rankingUrl) return;
    var self = this;
    fetch(this.config.rankingUrl, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (file) { self.applyRanking(file); })
      .catch(function () { /* leave the listing order — it is still a full shop */ });
  };

  /* The quiz can finish while this screen is already mounted (no redirect
     configured), so pick the answers up from the event too. */
  Results.prototype.onComplete = function (event) {
    if (!event.detail || !event.detail.answers) return;
    /* A navigation to the results URL is already on its way. Painting anything
       now only shows the shopper this screen twice — once over the quiz, then
       again when the new document lands. Leave the old page as it is. */
    if (event.detail.redirecting) return;
    this.selected = [];
    this.root.hidden = false;
    /* Handed over in place, so the URL never said `results` and the class the
       CSS switch keys off was never set. */
    document.documentElement.classList.add('quiz-lp-on-results');
    this.start();
  };

  /* --------------------------------------------------------------- ranking */

  /* The catalogue the ranking needs is already in the DOM — every card carries
     its style slug and the linked product's handle, the two keys quiz.json may
     use — so nothing has to be duplicated into a JSON blob. */
  Results.prototype.catalogue = function () {
    var out = { ladies: [], mens: [], kids: [] };
    this.items.forEach(function (item) {
      var card = item.querySelector('.quiz-lp-results__card');
      if (!card || !out[card.dataset.range]) return;
      out[card.dataset.range].push({ slug: card.dataset.cardSlug, child: card.dataset.cardChild });
    });
    return out;
  };

  /* Scored for EVERY range, not just the one the shopper picked. The
     measurements are the shopper's own glasses, so they say exactly as much
     about the women's rail as the men's — a shopper who answered "men's" can
     switch tab and still see which styles clear their frames, instead of an
     unmarked wall. The tabs still open on their own range; this only decides
     what carries a mark and in what order each range sits.

     Kids carries no bands in the ranking file, so rankStyles marks nothing
     there. That is right: one size, nothing to tell apart. */
  Results.prototype.applyRanking = function (rankingFile) {
    if (!window.QuizLP) return;

    var self = this;
    var answered = this.answers.range;
    var catalogue = this.catalogue();
    var position = {};

    RANGES.forEach(function (range) {
      if (!(catalogue[range] || []).length) return;

      /* The shopper's answers with the range swapped — everything else about
         them is unchanged, because their glasses have not changed. */
      var answers = {};
      Object.keys(self.answers).forEach(function (key) { answers[key] = self.answers[key]; });
      answers.range = range;

      window.QuizLP.rankStyles(rankingFile, answers, catalogue).forEach(function (entry, i) {
        position[range + ':' + entry.handle] = i;
        if (!entry.fits) return;
        var item = self.itemFor(range, entry.handle);
        var mark = item && item.querySelector('[data-fit-mark]');
        if (mark) mark.hidden = false;
      });
    });

    /* Every range is reordered within itself so what fits leads, and the
       shopper's own range still leads the grid as a whole. */
    var ordered = this.items.slice().sort(function (a, b) {
      var ar = a.dataset.range;
      var br = b.dataset.range;
      if (ar !== br) {
        if (ar === answered) return -1;
        if (br === answered) return 1;
        return RANGE_ORDER[ar] - RANGE_ORDER[br];
      }
      var ai = position[ar + ':' + a.querySelector('.quiz-lp-results__card').dataset.cardSlug];
      var bi = position[br + ':' + b.querySelector('.quiz-lp-results__card').dataset.cardSlug];
      return (ai == null ? 99 : ai) - (bi == null ? 99 : bi);
    });

    var frag = document.createDocumentFragment();
    ordered.forEach(function (item) { frag.appendChild(item); });
    this.grid.appendChild(frag);
    this.items = ordered;
  };

  Results.prototype.itemFor = function (range, slug) {
    for (var i = 0; i < this.items.length; i++) {
      var card = this.items[i].querySelector('.quiz-lp-results__card');
      if (card && card.dataset.range === range && card.dataset.cardSlug === slug) return this.items[i];
    }
    return null;
  };

  /* ------------------------------------------------------------------ head */

  Results.prototype.renderHead = function () {
    if (!this.lead) return;

    if (!this.answers) {
      this.lead.textContent = this.labels.leadNoAnswers || '';
      this.sub.innerHTML = this.retakeSentence(this.labels.subNoAnswers);
    } else if (this.answers.range === 'kids') {
      this.lead.textContent = this.labels.leadKids || '';
      this.sub.innerHTML = this.retakeSentence(this.labels.subKids);
    } else {
      this.lead.textContent = this.labels.lead || '';
      this.sub.innerHTML = this.retakeSentence(this.labels.sub);
    }

    this.renderAnswerChips();
  };

  /* The retake link is a button inside the sentence, so the copy reads as one
     line. [retake] marks where it goes; without the token it is appended, so a
     merchant cannot accidentally lose it. */
  Results.prototype.retakeSentence = function (text) {
    var link =
      '<button type="button" class="quiz-lp-results__retake" data-results-retake>' +
      esc(this.labels.retake) +
      '</button>';
    var body = esc(text || '');
    return body.indexOf('[retake]') >= 0 ? body.split('[retake]').join(link) : body + ' ' + link;
  };

  Results.prototype.renderAnswerChips = function () {
    if (!this.answersBox) return;
    var labels = this.answerLabels;
    if (!labels || !labels.length) {
      this.answersBox.hidden = true;
      return;
    }
    this.answersBox.hidden = false;
    this.answersList.innerHTML = labels
      .map(function (item) {
        return '<span>' + esc(item.label) + ': <b>' + esc(item.value) + '</b></span>';
      })
      .join('');
  };

  /* --------------------------------------------------------------- filters */

  Results.prototype.setFilter = function (range) {
    var known = this.filterBar.querySelector('[data-range="' + range + '"]');
    this.filter = known ? range : 'all';
    var filter = this.filter;

    this.filterBar.querySelectorAll('[data-range]').forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.dataset.range === filter));
    });

    var shown = 0;
    this.items.forEach(function (item) {
      var show = filter === 'all' || item.dataset.range === filter;
      item.hidden = !show;
      if (show) shown += 1;
    });
    if (this.emptyEl) this.emptyEl.hidden = shown > 0;
  };

  /* --------------------------------------------------------------- swatches */

  /* Swatch click → swap the card image, price, colour name, fitting-guide
     images and the variant the Select button stages. Ported from the B2S
     section, which owns the same card. */
  Results.prototype.bindSwatches = function () {
    var self = this;
    this.grid.querySelectorAll('.quiz-lp-results__card').forEach(function (card) {
      var swatches = card.querySelectorAll('.quiz-lp-results__swatch');
      if (!swatches.length) return;

      var addBtn = card.querySelector('.quiz-lp-results__add');
      var img = card.querySelector('.quiz-lp-results__media img');
      var colourEl = card.querySelector('[data-card-color]');
      var priceNow = card.querySelector('.quiz-lp-results__price-now');
      var priceWas = card.querySelector('.quiz-lp-results__price-was');
      var fgLink = card.querySelector('.quiz-lp-results__fg-link');

      swatches.forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          swatches.forEach(function (sw) { sw.classList.remove('is-on'); });
          swatch.classList.add('is-on');

          var d = swatch.dataset;
          if (img && d.variantImage) {
            img.removeAttribute('srcset'); /* or the new src never renders */
            img.src = d.variantImage;
          }
          /* Keep the fitting guide pointed at the selected colour's images, and
             hide it outright when that colour has none — no empty drawer. */
          if (fgLink && d.variantFgimages !== undefined) {
            fgLink.dataset.fgImages = d.variantFgimages;
            var has = d.variantFgimages && d.variantFgimages !== '[]';
            fgLink.hidden = !(has || (self.config.fittingGuide.fallback || []).length);
          }
          if (colourEl && d.variantTitle) colourEl.textContent = d.variantTitle;
          if (priceNow && d.variantPrice) priceNow.textContent = d.variantPrice;
          if (priceWas) {
            if (d.variantCompare) {
              priceWas.textContent = d.variantCompare;
              priceWas.hidden = false;
            } else {
              priceWas.hidden = true;
            }
          }
          if (addBtn && d.variantId) {
            addBtn.dataset.addVariantId = d.variantId;
            if (d.variantPriceCents) addBtn.dataset.price = d.variantPriceCents;
            /* Availability, then re-render the button for the NEW variant —
               the staged state is per-variant, so a colour change updates it. */
            if (d.variantAvailable === 'false') addBtn.setAttribute('data-unavailable', '');
            else addBtn.removeAttribute('data-unavailable');
            self.updateCardState(addBtn);
          }
        });
      });
    });
  };

  /* ------------------------------------------------------------- selection */

  Results.prototype.indexOfVariant = function (id) {
    var key = String(id);
    for (var i = 0; i < this.selected.length; i++) {
      if (this.selected[i].id === key) return i;
    }
    return -1;
  };

  Results.prototype.refreshCardStates = function () {
    this.grid.querySelectorAll('.quiz-lp-results__add').forEach(this.updateCardState, this);
  };

  Results.prototype.updateCardState = function (btn) {
    if (!btn) return;
    var card = btn.closest('.quiz-lp-results__card');

    /* A sold-out colour is never selectable. */
    if (btn.hasAttribute('data-unavailable')) {
      btn.disabled = true;
      btn.classList.remove('is-added');
      btn.setAttribute('aria-pressed', 'false');
      if (card) card.classList.remove('is-selected');
      btn.textContent = btn.dataset.soldOutLabel || 'Sold out';
      return;
    }

    /* The pressed state is VARIANT-specific: switching colour on a staged card
       leaves the new colour unstaged, which is what the shopper expects.
       A variant already in the cart counts as selected too — the card would
       otherwise un-press the instant the add succeeded, as though it had not
       worked. */
    var id = btn.dataset.addVariantId;
    var on = this.indexOfVariant(id) !== -1 ||
      this.isPending(id) ||
      (this.keepSelected && this.isInCart(id));
    btn.disabled = false;
    btn.classList.toggle('is-added', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (card) card.classList.toggle('is-selected', on);
    btn.textContent = on
      ? ((this.narrow.matches && btn.dataset.addedLabelShort) || btn.dataset.addedLabel || 'Remove')
      : ((this.narrow.matches && btn.dataset.addLabelShort) || btn.dataset.addLabel || 'Select this pair');
  };

  Results.prototype.toggleCard = function (btn) {
    if (!btn || btn.hasAttribute('data-unavailable')) return;
    var id = btn.dataset.addVariantId;
    if (!id) return;

    var name = btn.dataset.title || '';
    var at = this.indexOfVariant(id);

    /* Already bought: the card is pressed because the variant is in the cart,
       not because it is staged here. Cards do not remove cart lines — open the
       drawer so the shopper removes it there, and the card un-presses on the
       mirror that follows. */
    if (at === -1 && this.keepSelected && this.isInCart(id)) {
      this.openCartDrawer();
      return;
    }

    if (at !== -1) {
      this.selected.splice(at, 1);
      this.toast(fill(this.labels.toastRemoved, { name: name }));
    } else {
      this.selected.push({
        id: String(id),
        title: name,
        price: parseInt(btn.dataset.price, 10) || 0
      });
      this.toast(fill(this.labels.toastAdded, { name: name }));
    }
    this.refreshCardStates();
    this.renderDock();
  };

  /* ------------------------------------------------------------------ dock */

  /* What the shopper actually saves. Two modes, matching the B2S tray:
       'percent'      — the discount is simply X% of the order total.
       'bogo_percent' — every pairSize-th item free (the CHEAPEST go free, which
                        is how Shopify resolves BOGO), then X% off the rest. */
  Results.prototype.quote = function () {
    var pairSize = this.config.pairSize || 2;
    var extra = (this.config.extraDiscount || 0) / 100;

    /* Everything the shopper is buying, whether it is already in the cart or
       still staged on this page — the offer applies across the whole order, so
       the estimate has to see the whole order. Ascending, because the cheapest
       are the ones Shopify gives away. */
    var prices = this.cartPrices
      .concat(this.selected.map(function (item) { return item.price || 0; }))
      .concat(this.pending.map(function (item) { return item.price || 0; }))
      .sort(function (a, b) { return a - b; });

    var n = prices.length;
    var freeCount = Math.floor(n / pairSize);
    var gross = 0;
    var freeValue = 0;
    prices.forEach(function (price, i) {
      gross += price;
      if (i < freeCount) freeValue += price;
    });

    var paid = this.config.discountMode === 'bogo_percent' ? gross - freeValue : gross;
    var total = Math.round(paid * (1 - extra));
    /* What the struck-through figure compares against: the subtotal once the
       free pairs are off (so the badge reads as the percentage itself), or the
       undiscounted value of everything (so it counts the free pairs too). */
    var compare = this.config.compareBasis === 'full' ? gross : paid;

    return {
      n: n,
      staged: this.selected.length,
      free: freeCount,
      gross: compare,
      total: total,
      save: compare ? Math.round((1 - total / compare) * 100) : 0
    };
  };

  Results.prototype.renderDock = function () {
    if (!this.dock) return;
    var qty = this.dock.querySelector('[data-tray-qty]');
    var pricing = this.dock.querySelector('[data-tray-pricing]');
    var cta = this.dock.querySelector('[data-dock-cta]');
    var status = this.dock.querySelector('[data-tray-status]');
    var fill_ = this.dock.querySelector('[data-dock-fill]');
    var track = this.dock.querySelector('[data-dock-track]');

    var pairSize = this.config.pairSize || 2;
    var q = this.quote();
    var tokens = {
      count: q.n,
      free: q.free,
      unit: q.n === 1 ? this.labels.unitOne : this.labels.unitMany,
      save: q.save,
      total: this.money(q.total),
      gross: this.money(q.gross)
    };

    /* Pair complete = the free one is unlocked. Drives the green summary line
       and the full progress bar. */
    var complete = q.n > 0 && q.n % pairSize === 0;
    qty.classList.toggle('is-complete', complete);

    if (!q.n) {
      qty.textContent = this.labels.trayEmptyQty;
      pricing.innerHTML = this.labels.trayEmptyPill
        ? '<span class="quiz-lp-results__tray-save">' + esc(this.labels.trayEmptyPill) + '</span>'
        : '';
      cta.textContent = this.labels.trayCtaEmpty;
      cta.disabled = true;
      cta.setAttribute('aria-disabled', 'true');
      if (status) status.textContent = this.labels.trayEmptyQty;
    } else {
      qty.innerHTML = fill(complete ? this.labels.trayQtyEven : this.labels.trayQtyOdd, tokens);

      pricing.textContent = '';
      if (this.config.showPricing) {
        pricing.appendChild(el('span', 'quiz-lp-results__tray-now', this.money(q.total)));
        if (q.total < q.gross) {
          pricing.appendChild(el('s', 'quiz-lp-results__tray-was', this.money(q.gross)));
          pricing.appendChild(el('span', 'quiz-lp-results__tray-save', fill(this.labels.traySave, tokens)));
        }
      }

      /* {count} in the CTA means "how many am I about to add", not the total.
         With nothing new staged the button has nothing to add, so it becomes
         the way back into the cart instead of sitting there dead. */
      cta.textContent = q.staged > 0
        ? fill(this.labels.trayCta, { count: q.staged, free: q.free, unit: q.staged === 1 ? this.labels.unitOne : this.labels.unitMany, save: q.save, total: tokens.total, gross: tokens.gross })
        : this.labels.trayCtaCartOnly;
      cta.disabled = false;
      cta.removeAttribute('aria-disabled');
      if (status) {
        status.textContent =
          fill(complete ? this.labels.trayQtyEven : this.labels.trayQtyOdd, tokens).replace(/<[^>]*>/g, '') +
          (this.config.showPricing ? ' — ' + this.money(q.total) : '');
      }
    }

    if (fill_) {
      /* Empty → 0%, part-way through a pair → its share, pair complete → 100%. */
      var within = q.n % pairSize;
      var pct = q.n === 0 ? 0 : within === 0 ? 100 : Math.round((within / pairSize) * 100);
      fill_.style.width = pct + '%';
      fill_.classList.toggle('is-complete', complete);
      if (track) track.setAttribute('aria-valuenow', pct);
    }
  };

  Results.prototype.revealDock = function () {
    if (!this.dock) return;
    this.dock.classList.remove('is-away');
    document.body.classList.add('quiz-lp-has-dock');
  };

  Results.prototype.toast = function (message) {
    if (!this.toastEl || !message) return;
    var self = this;
    this.toastEl.textContent = message;
    this.toastEl.classList.add('is-show');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(function () { self.toastEl.classList.remove('is-show'); }, 1800);
  };

  /* ------------------------------------------------------------ cart mirror */

  /* Pull the live cart and mirror it onto the card states and the tray. Does
     NOT sync the discount — syncDiscount calls this, so that would recurse. */
  Results.prototype.mirrorCart = function () {
    var self = this;
    return this.fetchCart().then(function (cart) {
      self.cartItemCount = (cart && cart.item_count) || 0;
      self.cartVariantIds = {};
      self.cartPrices = [];
      ((cart && cart.items) || []).forEach(function (line) {
        self.cartVariantIds[String(line.variant_id || line.id)] = true;
        /* original_price, not final_price: the tray works out the offer itself,
           so it has to start from undiscounted unit prices or the discount gets
           counted twice. */
        var unit = (line.original_price != null ? line.original_price : line.price) || 0;
        for (var i = 0; i < (line.quantity || 1); i++) self.cartPrices.push(unit);
      });
      self.refreshCardStates();
      self.renderDock();
      /* Announced from here rather than from the add, so anything watching the
         cart — the header badge above all — follows a change made anywhere,
         including a removal in the drawer. The listeners re-read with a GET,
         which is not a mutation, so this cannot loop. */
      document.dispatchEvent(new CustomEvent('quiz-lp:cart-updated', { detail: { itemCount: self.cartItemCount } }));
      return cart;
    });
  };

  Results.prototype.isInCart = function (id) {
    return !!this.cartVariantIds[String(id || '')];
  };

  Results.prototype.isPending = function (id) {
    var key = String(id || '');
    for (var i = 0; i < this.pending.length; i++) {
      if (String(this.pending[i].id) === key) return true;
    }
    return false;
  };

  /* Any cart write anywhere on the page — a quantity change in the drawer, a
     remove, another section's add — re-mirrors us, so a card drops its selected
     state the moment its line leaves the cart. Both fetch and XHR are watched
     because the drawer app's choice is its own. The patches are global-once;
     the listener is per instance. */
  function watchCartMutations() {
    var MUTATION = /\/cart\/(change|update|add|clear)(\.js)?/;
    var fire = function () {
      try { window.dispatchEvent(new Event('quiz-lp:cartchanged')); } catch (e) { /* very old browser */ }
    };

    if (!window.__quizLpCartFetchPatched && window.fetch) {
      window.__quizLpCartFetchPatched = true;
      var origFetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var out = origFetch.apply(this, arguments);
        if (out && out.then && MUTATION.test(url)) out.then(fire, function () {});
        return out;
      };
    }

    if (!window.__quizLpCartXhrPatched && window.XMLHttpRequest) {
      window.__quizLpCartXhrPatched = true;
      var open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__quizLpCartUrl = url || '';
        return open.apply(this, arguments);
      };
      var send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        if (MUTATION.test(this.__quizLpCartUrl || '')) this.addEventListener('loadend', fire);
        return send.apply(this, arguments);
      };
    }
  }

  /* -------------------------------------------------------------- discounts */

  Results.prototype.fetchCart = function () {
    return fetch(cartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  };

  /* 'active'   → every code is on the cart and applicable
     'inactive' → on the cart but rejected (e.g. attached while the cart was
                  below the minimum) — needs re-applying now that it qualifies
     'absent'   → at least one is not on the cart at all
     With several codes the cart only counts as sorted when ALL of them are
     attached and applicable; otherwise we keep retrying. */
  Results.prototype.codeState = function (cart) {
    var listed = (cart && cart.discount_codes) || [];
    var seen = {};
    listed.forEach(function (entry) {
      seen[String(entry.code || '').toUpperCase()] =
        entry.applicable === false ? 'inactive' : 'active';
    });

    var anyInactive = false;
    for (var i = 0; i < this.discountCodes.length; i++) {
      var state = seen[this.discountCodes[i].toUpperCase()];
      if (!state) return 'absent';
      if (state === 'inactive') anyInactive = true;
    }
    return anyInactive ? 'inactive' : 'active';
  };

  /* One request per code, in the merchant's order, rather than a comma-joined
     path: combinable discounts accumulate this way, and it avoids depending on
     how Shopify splits a comma inside a path once a code itself needs encoding.

     It MUST be a fetch, not an iframe navigation — storefront documents are
     served with CSP `frame-ancestors 'none'`, so framing /discount/CODE is
     blocked outright and never runs. */
  Results.prototype.applyDiscount = function () {
    var root = cartRoot();
    return this.discountCodes.reduce(function (chain, code) {
      return chain.then(function () {
        /* Decode first: a code pasted already-encoded ("A%20B"), or one that
           round-tripped through a URL, would otherwise be double-encoded into
           the "A%2520B" Shopify can never match. */
        var clean = code;
        try {
          var decoded = decodeURIComponent(code);
          if (decoded) clean = decoded;
        } catch (e) {
          /* not encoded — use it as typed */
        }
        var url = root + 'discount/' + encodeURIComponent(clean) +
          '?redirect=' + encodeURIComponent(root + 'cart.js');
        return fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          redirect: 'follow'
        }).catch(function () { /* the verify below decides whether it landed */ });
      });
    }, Promise.resolve());
  };

  /* Nudge the drawer to re-read the cart once the discount lands, so it cannot
     sit there rendering pre-discount totals. */
  Results.prototype.notifyCartUpdated = function () {
    try {
      if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    } catch (e) {
      /* no drawer app on the page */
    }
    document.dispatchEvent(new CustomEvent('quiz-lp:cart-updated'));
  };

  /* The request completing is not proof the code took, so every attempt is
     verified against a fresh cart read before it is believed. */
  Results.prototype.applyAndVerify = function (attempt) {
    var self = this;
    return this.applyDiscount()
      .then(function () {
        self.appliedThisLoad = true;
        return self.fetchCart();
      })
      .then(function (fresh) {
        if (fresh && self.codeState(fresh) === 'active') {
          self.notifyCartUpdated();
          return true;
        }
        if (attempt >= DISCOUNT_BACKOFF.length) return false;
        return new Promise(function (r) { setTimeout(r, DISCOUNT_BACKOFF[attempt]); })
          .then(function () { return self.applyAndVerify(attempt + 1); });
      });
  };

  /* The single entry point. Safe to call as often as you like — it no-ops when
     the cart is too small or the codes are already live. ALWAYS return this
     promise to whatever is about to render the cart, or the drawer paints stale
     totals while the request is still in flight. */
  Results.prototype.syncDiscount = function () {
    if (!this.discountEnabled) return Promise.resolve();
    if (this.discountInFlight) return this.discountInFlight;

    var self = this;
    this.discountInFlight = this.fetchCart()
      .then(function (cart) {
        var count = (cart && cart.item_count) || 0;
        /* Below the minimum the codes would just be rejected — wait. */
        if (count < self.discountMinItems) return null;
        /* Only skip once we have proven the codes took on THIS load. */
        if (self.appliedThisLoad && self.codeState(cart) === 'active') return null;
        return self.applyAndVerify(0);
      })
      .catch(function () { return null; })
      .then(function (value) {
        self.discountInFlight = null;
        return value;
      });
    return this.discountInFlight;
  };

  /* Codes into the session BEFORE the cart is built, so the totals are right the
     first time the drawer paints. Once per page load is enough, and it
     deliberately does not set appliedThisLoad: a seeded session is not proof the
     cart accepted anything, so the post-add sync still verifies. */
  Results.prototype.seedDiscount = function () {
    if (!this.discountEnabled || !this.config.discount.prewarm || this.discountSeeded) {
      return Promise.resolve();
    }
    this.discountSeeded = true;
    return this.applyDiscount();
  };

  /* Empty the cart so every visitor starts this page at zero. Through the
     silent fetch, so the drawer app does not react to it and pop open over the
     quiz. Note that cart/clear.js is enough on its own: the /discount/ route
     holds one set of codes at a time, so ours overwrite whatever the shopper
     arrived with — posting {discount:''} to cart/update.js is undocumented and
     is the prime suspect for a "first apply never sticks" bug. */
  Results.prototype.clearCartOnLoad = function () {
    var self = this;
    /* Switched off: the cart the shopper arrived with stands, and the mirror
       that follows picks it up. */
    if (!this.config.discount.clearCartOnLoad) return Promise.resolve();
    return silentFetch(window.location.origin + cartRoot() + 'cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
    })
      .then(function () { self.notifyCartUpdated(); })
      .catch(function () { /* leave whatever the shopper had */ });
  };

  /* ------------------------------------------------------------------ cart */

  /* UpCart publishes plain globals rather than one namespaced object, so the
     API comes first and a DOM trigger is only the fallback — this page drops
     the theme header, so there is usually no cart icon to click. The last
     resort navigates, so the button is never dead. */
  Results.prototype.openCartDrawer = function () {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    try {
      if (typeof window.upcartOpenCart === 'function') { window.upcartOpenCart(); return; }
      var api = window.UpCart || window.upcart;
      if (api && typeof api.open === 'function') { api.open(); return; }
    } catch (e) {
      /* fall through to the DOM trigger */
    }
    var trigger = document.querySelector(
      '#upcart-toggle, [data-upcart-toggle], .upcart-toggle, #cart-icon-bubble, .header__icon--cart, a[href$="/cart"]'
    );
    if (trigger) { trigger.click(); return; }
    window.location.href = root + 'cart';
  };

  /* Deliberately NOT a Liquid product form: a real <form action="/cart/add">
     is what half the storefront's apps bind to, and submitting one here set
     them off. This posts to /cart/add.js and nothing else. */
  Results.prototype.addToCart = function () {
    var self = this;
    /* Nothing new to add, but the cart already holds pairs — the CTA is the way
       back into the drawer at that point. */
    if (!this.selected.length) {
      if (this.cartItemCount > 0) this.openCartDrawer();
      return;
    }
    this.drawerDismissed = false;
    var cta = this.dock.querySelector('[data-dock-cta]');
    var added = this.selected.length;
    var items = this.selected.map(function (item) {
      return { id: Number(item.id), quantity: 1 };
    });
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    cta.classList.add('is-loading');
    cta.disabled = true;

    /* Hand the staged pairs to `pending` rather than dropping them: the cards
       stay pressed and the tray keeps its total while the request is in flight,
       instead of blinking empty until cart.js catches up. */
    this.pending = this.selected.slice();
    this.selected = [];
    this.refreshCardStates();
    this.renderDock();

    /* Wait on the load-time clear, seed the codes into the session, THEN build
       the cart — so the very first cart the drawer shows is already discounted,
       rather than painting full price and correcting itself a beat later. */
    this.cartReady
      .then(function () { return self.seedDiscount(); })
      /* /cart/add.js ADDS — it never replaces, so anything already in the cart
         and its line properties survive. */
      .then(function () {
        return fetch(window.location.origin + root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data && data.status) throw new Error(data.description || 'add failed');
        /* The cart is authoritative from here — drop the mid-flight copy and
           re-mirror, which is what repaints the cards and the tray. */
        return self.mirrorCart().then(function () { self.pending = []; return null; });
      })
      .then(function () {
        self.refreshCardStates();
        self.renderDock();
        self.toast(
          fill(self.labels.toastAddedToCart, {
            count: added,
            unit: added === 1 ? self.labels.unitOne : self.labels.unitMany
          })
        );
        document.dispatchEvent(new CustomEvent('quiz-lp:cart-added', { detail: { items: items } }));

        if (self.config.afterAdd === 'cart') { window.location.href = root + 'cart'; return null; }
        if (self.config.afterAdd === 'checkout') { window.location.href = root + 'checkout'; return null; }
        if (self.config.afterAdd === 'stay') return null;

        /* Give the discount a head start so the drawer is unlikely to paint
           pre-discount totals — but never block the open on it. Holding the
           button for the full retry backoff is what makes it look stuck. */
        return Promise.race([
          self.syncDiscount(),
          new Promise(function (r) { setTimeout(r, DRAWER_DISCOUNT_WAIT); })
        ]).then(function () {
          /* Nudge the drawer to re-read the cart in case it missed our fetch,
             then open it — unless it is already open (re-triggering toggles
             some builds shut) or the shopper closed it while we worked. */
          try {
            if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
          } catch (e) {
            /* the open below still shows whatever the app has */
          }
          if (self.drawerOpen || self.drawerDismissed) return;
          self.openCartDrawer();
        });
      })
      .catch(function () {
        /* Put the pairs back so nothing is silently lost. */
        self.selected = self.pending.concat(self.selected);
        self.pending = [];
        self.refreshCardStates();
        self.renderDock();
        self.toast(self.labels.toastError);
      })
      .then(function () { cta.classList.remove('is-loading'); });
  };

  /* ---------------------------------------------------------------- events */

  Results.prototype.onClick = function (event) {
    var target = event.target;

    if (target.closest('[data-results-retake]')) {
      if (window.QuizLP) window.QuizLP.clearResult();
      window.location.href = this.config.quizUrl || window.location.pathname;
      return;
    }

    var filter = target.closest('[data-range]');
    if (filter && this.filterBar.contains(filter)) {
      this.setFilter(filter.dataset.range);
      return;
    }

    var guide = target.closest('[data-fg-open]');
    if (guide) {
      this.openFittingGuide(guide);
      return;
    }

    if (target.closest('[data-dock-cta]')) {
      this.addToCart();
      return;
    }

    /* The whole card is the control, except the parts that own their own
       behaviour — swatches, the fitting-guide link, real links. The Select
       button is inside the card, so it falls through to the same toggle. */
    var card = target.closest('[data-card-toggle]');
    if (!card) return;
    if (target.closest('.quiz-lp-results__swatch, .quiz-lp-results__fg-link, a')) return;
    this.toggleCard(card.querySelector('.quiz-lp-results__add'));
  };

  Results.prototype.openFittingGuide = function (button) {
    var images = [];
    try { images = JSON.parse(button.dataset.fgImages || '[]'); } catch (e) { /* fall back below */ }
    if (!images.length) images = this.config.fittingGuide.fallback || [];
    if (!window.SaintFG) return;
    window.SaintFG.open({
      title: this.config.fittingGuide.title,
      productName: button.dataset.fgTitle || '',
      bodyHtml: this.config.fittingGuide.bodyHtml,
      images: images
    });
  };

  /* ---------------------------------------------------------------- helpers */

  function fill(template, tokens) {
    var out = String(template || '');
    Object.keys(tokens || {}).forEach(function (key) {
      out = out.split('{' + key + '}').join(tokens[key] == null ? '' : tokens[key]);
    });
    return out;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    node.className = className;
    node.textContent = text;
    return node;
  }

  /* ----------------------------------------------------------------- boot */

  function init(scope) {
    (scope || document).querySelectorAll('[data-quiz-lp-results]').forEach(function (root) {
      if (root.dataset.quizLpResultsReady) return;
      root.dataset.quizLpResultsReady = 'true';
      new Results(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
